import React, { useEffect, useState } from 'react';
import Button from 'components/Button';
import Form, { FormSubmitHandler } from 'components/Form/Form';
import FormInput from 'components/Form/FormInput';
import ZephrService, { ZephrRegistrationData } from 'services/ZephrService';
import SocialLoginButtons from 'components/SocialLogin/SocialLoginButtons';
import Typography from 'components/Typography';

import { FormValidator } from 'components/Form/FormValidator';
import { SocialLoginMessageData } from 'interfaces/social-login';

import { renderTermsOfServiceLink } from 'utils/renderTermsOfServiceLink';
import { renderPrivacyPolicyLink } from 'utils/renderPrivacyPolicyLink';
import styles from './RegisterForm.module.scss';
import Email from "../SvgIcons/Email";

type FormFields = {
  email: string;
  password: string;
};

const defaultValues: FormFields = {
  email: '',
  password: '',
};

export interface RegisterFormProps {
  registrationTitle: string;
  registrationText: string;
  showLoginForm?: Function;
}

const finishSocialLogin = async (data: SocialLoginMessageData) => {
  if (!window.ZephrConfiguration?.apiBaseUrl) {
    document.cookie = data.cookie;

    // timeout because it can take browser a moment to set the cookie
    return setTimeout(
      () => ({
        status: 200,
        data: {},
      }),
      20
    );
  }

  const response = await fetch(`${window.ZephrConfiguration?.apiBaseUrl}/api/finish-social-login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  const responseData = await response.json();

  if (response.status !== 200) {
    return {
      status: response.status,
      data: null,
      error: responseData?.message,
    };
  }

  return {
    status: response.status,
    data: responseData,
  };
};

const RegisterForm: React.FC<RegisterFormProps> = ({ registrationText, registrationTitle, showLoginForm }) => {
  const [emailError, setEmailError] = useState<string | undefined>('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [resendDisabled, setResendDisabled] = useState(true);
  const [showEmailRegForm, setShowEmailRegForm] = useState(false);

  const onEmailRegisterClick = () => {
    setShowEmailRegForm(true);
  };

  const onLoginFormSwitch = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (showLoginForm) {
      showLoginForm();
    }
  };

  const registerUser = async (regData: ZephrRegistrationData) => {
    // register user on Zephr
    const response = await ZephrService.register(regData);

    if (response.status >= 300) {
      // show error
      switch (response.status) {
        case 409:
          setEmailError('E-posten er allerede i bruk');
          break;
        default:
          setEmailError('Feil ved registrering');
      }

      return;
    }

    // if using social registration just reload the page
    if (!regData.identifiers?.email_address) {
      window.location.reload();

      return;
    }

    // trigger email verification if registering with email
    const verifyResponse = await ZephrService.sendVerificationEmail({
      email: regData.identifiers.email_address,
      redirectUrl: window.location.href,
    });

    if (!verifyResponse.success) {
      setEmailError(verifyResponse.error);

      return;
    }

    setUserEmail(regData.identifiers.email_address);
    setRegistrationSuccess(true);

    // in 30sec enable resend button
    setTimeout(() => {
      setResendDisabled(false);
    }, 30000);
  };

  const onRegisterMessage = (message: MessageEvent) => {
    if (message.data.status === 'failure') {
      // show error message - message.data.message
      // eslint-disable-next-line no-console
      console.log('======= POST MESSAGE fail', message.data);

      return;
    }

    if (message.data.action === 'register') {
      // eslint-disable-next-line no-console
      console.log('======= POST MESSAGE REGISTER', message.data);

      const regData: ZephrRegistrationData = {
        attributes: {
          'first-name': message.data?.userAttributes?.['first-name'],
          'last-name': message.data?.userAttributes?.['last-name'],
        },
        validators: {
          token_exchange: message.data.stateKey,
        },
      };

      registerUser(regData).catch(e => {
        // eslint-disable-next-line no-console
        console.error('Registration error', e.message);
      });
    }

    if (message.data.action === 'login') {
      finishSocialLogin(message.data).then(() => {
        window.location.reload();
      });
    }
  };

  const handleSubmit: FormSubmitHandler<FormFields> = async (data): Promise<void> => {
    setEmailError('');

    const regData: ZephrRegistrationData = {
      identifiers: {
        email_address: data.email,
      },
      validators: {
        password: data.password,
      },
    };

    return registerUser(regData).catch(e => {
      // eslint-disable-next-line no-console
      console.error('Registration error', e.message);
    });
  };

  const resendVerificationEmail = async () => {
    setResendDisabled(true);

    await ZephrService.sendVerificationEmail({
      email: userEmail,
      redirectUrl: window.location.href,
    });

    setResendDisabled(false);
  };

  useEffect(() => {
    window.addEventListener('message', onRegisterMessage);

    return () => {
      window.removeEventListener('message', onRegisterMessage);
    };
  });

  return (
    <div className={styles.container}>
      {!registrationSuccess ? (
        <>
          <Typography variant="h3" className={styles.title}>
            {registrationTitle}
          </Typography>
          <Typography variant="body1" color="grey-900" className={styles.text}>
            {registrationText}
          </Typography>
          { showEmailRegForm ? (
              <div className={styles.registrationForm}>
                <Form defaultValues={defaultValues} onSubmit={handleSubmit}>
                  {({ formState: { isSubmitting } }) => (
                    <>
                      <FormInput
                        name="email"
                        type="text"
                        label="E-post *"
                        validate={FormValidator.all(FormValidator.isNotEmpty, FormValidator.isValidEmail)}
                      />
                      {emailError && <p className={styles.formError}>{emailError}</p>}
                      <FormInput
                        name="password"
                        type="password"
                        label="Opprett passord *"
                        validate={FormValidator.all(FormValidator.isNotEmpty, FormValidator.minLength(8))}
                      />
                      <Button
                        id="btn-register-continue-submit"
                        type="submit"
                        disabled={isSubmitting}
                        loading={isSubmitting}
                        fullWidth
                      >
                        Fortsett
                      </Button>
                    </>
                  )}
                </Form>
                <Typography variant="body3" color="grey-900" className={styles.terms}>
                  Ved å fortsette aksepterer du våre{' '}
                  <a href={renderTermsOfServiceLink(window.location.hostname)}>brukervilkår</a> og{' '}
                  <a href={renderPrivacyPolicyLink(window.location.hostname)}>personvernserklæring</a>
                </Typography>
                <div className={styles.divider} />
              </div>
          ) : (
            <Button
            id="btn-login-with-email"
            onClick={onEmailRegisterClick}
            variant="outlined"
            startIcon={<Email className={styles.icon} />}
            className={styles.emailRegBtn}
            fullWidth
            >
            Fortsett med e-post
            </Button>
            )}
          <SocialLoginButtons />
          <Typography variant="body1" className={styles.registerLink}>
            Har du allerede en konto?{' '}
            <a href="/zephr/login" onClick={onLoginFormSwitch} id="btn-switch-to-login">
              Logg inn
            </a>
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="h3" className={styles.title}>
            Bekreft din e-post adresse
          </Typography>
          <Typography variant="body1" color="grey-900" className={styles.verifyText}>
            Du er snart i mål. Vi har sendt en e-post til <span className={styles.email}>{userEmail}</span>. Klikk på
            linken i e-posten for å fullføre registreringen
          </Typography>
          <Typography variant="body1" color="grey-900" className={styles.verifyText}>
            Mottok du ikke?
          </Typography>
          {emailError && <p className={styles.formError}>{emailError}</p>}
          <Button
            type="button"
            fullWidth
            disabled={resendDisabled}
            className={styles.resendButton}
            onClick={resendVerificationEmail}
          >
            Send e-post på nytt
          </Button>
        </>
      )}
    </div>
  );
};

export default RegisterForm;
